import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services';

/**
 * Auth Context
 * Provides authentication state, permissions, menus, and methods to the application
 */
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [menus, setMenus] = useState([]); // Accessible menus (user has access to)
  const [menuIds, setMenuIds] = useState([]); // Accessible menu IDs
  const [allAvailableMenus, setAllAvailableMenus] = useState([]); // All menus for display
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Helper function to safely parse menu IDs from response/storage
  const parseMenuIds = (ids) => {
    if (!ids) return [];
    if (typeof ids === 'string') {
      try {
        // Handle stringified array like "[1,2,3]"
        const parsed = JSON.parse(ids);
        return Array.isArray(parsed) ? parsed.map(Number) : [];
      } catch (e) {
        // If it's not JSON, might be a single number as string
        const num = Number(ids);
        return isNaN(num) ? [] : [num];
      }
    }
    return Array.isArray(ids) ? ids.map(Number) : [];
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    const storedUser = authService.getStoredUser();
    const token = localStorage.getItem('accessToken');

    if (storedUser && token) {
      setUser(storedUser);
      setMenuIds(parseMenuIds(storedUser.menuIds));
      setMenus(storedUser.menus || []);
      setAllAvailableMenus(storedUser.allMenus || []);
      setIsAuthenticated(true);

      // Somebody still signed in from before multi-site groups existed has no
      // site list stored. Fetch it once so the switcher appears without making
      // them sign in again. Platform admins have no sites.
      const roleCode = storedUser.role?.role_code;
      if (storedUser.sites === undefined && roleCode !== 'admin' && roleCode !== 'super_admin') {
        authService.getMySites()
          .then((res) => {
            if (!res?.success) return;
            const next = { ...storedUser, sites: res.data.sites, active_site_id: res.data.active_site_id };
            localStorage.setItem('user', JSON.stringify(next));
            setUser(next);
          })
          .catch(() => { /* no switcher is the safe fallback */ });
      }
    }

    setLoading(false);
  }, []);

  const login = async (user_id, pin) => {
    try {
      const response = await authService.login(user_id, pin);

      if (response.success) {
        setUser(response.data.user);
        const parsedMenuIds = parseMenuIds(response.data.user.menuIds);
        setMenuIds(parsedMenuIds);
        setMenus(response.data.user.menus || []);
        setAllAvailableMenus(response.data.user.allMenus || []);
        setIsAuthenticated(true);

        return { success: true, data: response.data };
      }

      return { success: false, message: response.message };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || 'Login failed',
      };
    }
  };

  /**
   * Multi-site groups: open another site. Everything on screen belongs to the
   * site being left, so the app restarts on the new site's landing page rather
   * than leaving a half-loaded page from the old one.
   */
  const switchSite = async (siteId) => {
    const response = await authService.switchSite(siteId);
    if (response?.success) {
      window.location.assign('/');
    }
    return response;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setMenuIds([]);
    setMenus([]);
    setAllAvailableMenus([]);
    setIsAuthenticated(false);
  };

  // Helper function to check if user has menu access
  const hasMenuAccess = (menuId) => {
    return menuIds.includes(menuId);
  };

  // Get accessible menus only (menus user has access to)
  const getAccessibleMenus = () => {
    return menus;
  };

  // Helper function to check if menu is active/accessible
  const isMenuActive = (menuId) => {
    return menuIds.includes(menuId);
  };

  // Check if user is admin
  const isAdmin = () => {
    const roleCode = user?.role?.role_code;
    return roleCode === 'admin' || roleCode === 'super_admin';
  };

  const value = {
    user,
    menus, // Accessible menus
    menuIds, // Accessible menu IDs
    allAvailableMenus, // All menus for display
    loading,
    isAuthenticated,
    login,
    logout,
    // Multi-site groups. One site (or none) means no switcher.
    sites: user?.sites || [],
    activeSiteId: user?.active_site_id ?? user?.organization?.id ?? null,
    switchSite,
    hasMenuAccess,
    getAccessibleMenus,
    isMenuActive,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
